import { useSyncPlanContext } from "../../path/sync/SyncPlanContext"
import { Change, FromCohort, PathItem } from "../../path/sync/types"
import {
  useCanDesignSourcePath,
  useShowSyncOption
} from "../../path/sync/utils"

export type StepPublishStatus =
  | "contentEmpty"
  | "contentUnpublished"
  | "newStep"
  | "published"
  | "contentDraft"
  | "stepDraft"

export const stepPublishStatuses: Record<StepPublishStatus, StepPublishStatus> =
  {
    contentEmpty: "contentEmpty",
    contentUnpublished: "contentUnpublished",
    contentDraft: "contentDraft",
    newStep: "newStep",
    stepDraft: "stepDraft",
    published: "published"
  }

export const useGetPublishStatus = (
  item: PathItem,
  cohort?: FromCohort
): StepPublishStatus => {
  let status = ""

  const { forward } = useSyncPlanContext()

  const userCanDesignSourcePath = useCanDesignSourcePath({
    cohortId: cohort?.id
  })
  // A user (ex: a Cohort Teacer) that has design access to the Cohort Path but not the Source Path
  // should not be able to view the source item's Blocks content status, since they can't edit the item's Blocks.
  const isViewingSourceItem =
    !userCanDesignSourcePath && item.id !== item.source_id

  const isLesson = item.type === "lesson"

  const isPublished = !!item.last_published_dtime

  const groupedChanges = forward?.groupedChanges

  if (!groupedChanges) {
    if (isPublished) return "published" as StepPublishStatus
    return "newStep" as StepPublishStatus
  }

  const { stepsNoContent, stepsUnpublishedBlocks, stepsDraftBlocks, syncable } =
    groupedChanges

  const changeListHasItem = (changeList: Change[]) => {
    return !!changeList?.find(
      (change: any) => change.item_source_id === item.source_id
    )
  }

  const hasChildChanges =
    isLesson &&
    !!groupedChanges.syncable.find(
      (change: Change) => change.parent_source_id === item.source_id
    )

  // If the user (example: a Cohort Teacher) has design access only to the Cohort Path but not the Source Path
  // and the item is sourced from the Source Path (but not opted out for this Cohort Path),
  // then the item is considered published (for the sake of the Cohort Path).
  if (changeListHasItem(stepsNoContent) && !isViewingSourceItem) {
    status = stepPublishStatuses.contentEmpty
  } else if (
    changeListHasItem(stepsUnpublishedBlocks) &&
    !isViewingSourceItem
  ) {
    status = stepPublishStatuses.contentUnpublished
  } else if (changeListHasItem(stepsDraftBlocks) && !isViewingSourceItem) {
    status = stepPublishStatuses.contentDraft
  } else if (!isPublished) {
    status = stepPublishStatuses.newStep
  } else if (changeListHasItem(syncable) || hasChildChanges) {
    status = stepPublishStatuses.stepDraft
  } else {
    status = stepPublishStatuses.published
  }

  return status as StepPublishStatus
}

export type ItemSyncStatus =
  | "none" // Applies to Source Path items and unchanged Cohort Path items
  | "unique" // Refers to a Path item that is unique to the current cohort
  | "changed" // Path item that is sourced and has changes; changed sourced Path items have a curriculum_sync_opt_out_dtime value
  | "synced" // Path item that is sourced and has no changes
  | "changed-viewonly" // Path item that is sourced and has changes; view-only (ie, for Cohort Teachers)
  | "synced-viewonly" // Path item that is sourced and has no changes; view-only (ie, for Cohort Teachers)

export const useGetSyncStatus = (
  item: PathItem,
  cohort: FromCohort
): ItemSyncStatus => {
  const canShowSync = useShowSyncOption({
    cohortId: cohort?.id
  })

  const userCanDesignSourcePath = useCanDesignSourcePath({
    cohortId: cohort?.id
  })
  // If the user that has design access to the Cohort Path but not the Source Path (ex: a Cohort Teacer)
  // and the item is source, then they should have view-only access to the item's content.
  const isViewingSourceItem =
    !userCanDesignSourcePath && item.id !== item.source_id
  const noContent = !item.content_id
  const isSourcedStep = item.id !== item.source_id // Path item is sourced from another cohort (ie, the Source Path),
  const isOptedOut = !!item.curriculum_sync_opt_out_dtime

  if (isViewingSourceItem && !isOptedOut) return "synced-viewonly"
  if (isViewingSourceItem && isOptedOut) return "changed-viewonly"

  if (!canShowSync) return "none"
  if (noContent) return "none"
  if (isSourcedStep && isOptedOut) return "changed"
  if (!isSourcedStep) return "unique"
  if (isSourcedStep && !isOptedOut) return "synced"

  return "none"
}
